import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { format } from "date-fns";
import { toast } from "sonner";

interface LocationVisitsProps {
  locationId: Id<"serviceLocations">;
  onScheduleVisit: () => void;
  onEditVisit: (visit: any) => void;
}

export function LocationVisits({ locationId, onScheduleVisit, onEditVisit }: LocationVisitsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch events for this location
  const events = useQuery(api.calendar.getEventsByLocationId, { locationId }) || [];

  // Delete event mutation
  const deleteEvent = useMutation(api.calendar.deleteEvent);

  // Filter for visit-related events only
  const visits = events.filter(
    (event: any) =>
      event.eventType === "walkthrough" ||
      event.eventType === "service" ||
      event.eventType === "maintenance" ||
      event.eventType === "meeting" ||
      event.eventType === "other"
  );

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case "walkthrough":
        return "bg-blue-100 text-blue-800";
      case "service":
        return "bg-green-100 text-green-800";
      case "maintenance":
        return "bg-orange-100 text-orange-800";
      case "meeting":
        return "bg-purple-100 text-purple-800";
      case "other":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case "walkthrough":
        return "Walkthrough";
      case "service":
        return "Service Visit";
      case "maintenance":
        return "Maintenance";
      case "meeting":
        return "Meeting";
      case "other":
        return "Other";
      default:
        return "Visit";
    }
  };

  const formatEventDateTime = (
    startDate: string,
    startTime: string,
    endTime: string,
    isAllDay: boolean
  ) => {
    // Parse the date string as local date to avoid timezone issues
    const [year, month, dayOfMonth] = startDate.split("-").map(Number);
    const date = new Date(year, month - 1, dayOfMonth); // month is 0-indexed
    const formattedDate = format(date, "MMM d, yyyy");

    if (isAllDay) {
      return `${formattedDate} (All day)`;
    }

    return `${formattedDate} at ${startTime} - ${endTime}`;
  };

  const handleDeleteVisit = (visit: any) => {
    setVisitToDelete(visit);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVisit = async () => {
    if (!visitToDelete) return;

    setIsDeleting(true);
    try {
      await deleteEvent({ id: visitToDelete._id });
      toast.success("Visit deleted successfully");
      setDeleteDialogOpen(false);
      setVisitToDelete(null);
    } catch (error) {
      console.error("Error deleting visit:", error);
      toast.error("Failed to delete visit");
    } finally {
      setIsDeleting(false);
    }
  };

  if (visits.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Visits</h3>
          <Button onClick={onScheduleVisit} className="gap-2">
            <Plus className="h-4 w-4" />
            Schedule Visit
          </Button>
        </div>
        <EmptyState
          icon={Calendar}
          title="No visits scheduled"
          description="Schedule a walkthrough, service visit, or maintenance appointment to get started."
          onAction={onScheduleVisit}
          actionLabel="Schedule Visit"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Visits ({visits.length})</h3>
        <Button onClick={onScheduleVisit} className="gap-2">
          <Plus className="h-4 w-4" />
          Schedule Visit
        </Button>
      </div>

      <div className="space-y-3">
        {visits.map((visit: any) => (
          <Card key={visit._id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="mb-2 flex items-center gap-2">
                  <h4 className="font-medium">{visit.title}</h4>
                  <Badge className={getEventTypeColor(visit.eventType)}>
                    {getEventTypeLabel(visit.eventType)}
                  </Badge>
                </div>

                {visit.description && (
                  <p className="mb-3 text-sm text-gray-600">{visit.description}</p>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {formatEventDateTime(
                        visit.startDate,
                        visit.startTime,
                        visit.endTime,
                        visit.isAllDay
                      )}
                    </span>
                  </div>

                  {(visit.location || visit.address) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {visit.location && visit.address
                          ? `${visit.location}, ${visit.address}`
                          : visit.location || visit.address}
                      </span>
                    </div>
                  )}

                  {visit.assignedStaffDetails && visit.assignedStaffDetails.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>Assigned to: {visit.assignedStaffDetails.join(", ")}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEditVisit(visit)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Visit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteVisit(visit)}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Visit
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this visit and remove it
              from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteVisit}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
